import { useState, useEffect, type FormEvent } from 'react'
import { Save, AlertCircle } from 'lucide-react'
import { messaggioErrore } from '../../lib/api'
import { Drawer } from '../../components/ui/Drawer'
import { Button } from '../../components/ui/Button'
import { VoiceSelector } from '../../components/voice/VoiceSelector'
import { useMieiLead } from '../../hooks/useMieiLead'
import { useToast } from '../../stores/toastStore'
import type { LeadAdmin } from '../../lib/tipi'
import styles from '../superadmin/LeadForm.module.css'

interface Props {
  aperto: boolean
  /** Se presente, modifica il lead esistente; altrimenti crea nuovo. */
  lead?: LeadAdmin | null
  /** Numero rimanente (per il titolo "X rimanenti"). null = illimitato. */
  rimanenti: number | null
  onChiudi: () => void
  onSalvato: () => void
}

/**
 * Drawer per creare/modificare un lead PRIVATO dell'SDR.
 *
 * Riusa lo stesso CSS del LeadForm superadmin (sezioni, label, tag).
 * Differenze dalla versione admin:
 *  - niente toggle `is_public` (sempre false)
 *  - niente campo `interno` (assegnato dal backend)
 *  - titolo mostra il contatore rimanenti
 *  - chiama `/leads` (non `/superadmin/leads`)
 */
export function MioLeadForm({ aperto, lead, rimanenti, onChiudi, onSalvato }: Props) {
  const editing = !!lead
  const { crea, modifica } = useMieiLead()
  const toast = useToast()

  const [nome, setNome] = useState('')
  const [ruolo, setRuolo] = useState('')
  const [settore, setSettore] = useState('')
  const [aziendaTipo, setAziendaTipo] = useState('')
  const [personalita, setPersonalita] = useState('')
  const [descrizioneEstesa, setDescrizioneEstesa] = useState('')
  const [difficolta, setDifficolta] = useState<1 | 2 | 3>(2)
  const [voiceId, setVoiceId] = useState<string | undefined>(undefined)

  const [obiezioni, setObiezioni] = useState<string[]>([])
  const [nuovaObiezione, setNuovaObiezione] = useState('')

  const [obiettivoChiamata, setObiettivoChiamata] = useState('')
  const [obiettiviPossibili, setObiettiviPossibili] = useState<string[]>([])
  const [nuovoObiettivo, setNuovoObiettivo] = useState('')

  const [erroreGlobale, setErroreGlobale] = useState<string | null>(null)

  useEffect(() => {
    if (!aperto) return
    if (lead) {
      setNome(lead.nome)
      setRuolo(lead.ruolo)
      setSettore(lead.settore)
      setAziendaTipo(lead.aziendaTipo ?? '')
      setPersonalita(lead.personalita)
      setDescrizioneEstesa(lead.descrizioneEstesa ?? '')
      setDifficolta(lead.difficolta)
      setVoiceId(lead.voiceId ?? undefined)
      setObiezioni(lead.obiezioni)
      setObiettivoChiamata(lead.obiettivoChiamata ?? '')
      setObiettiviPossibili(lead.obiettiviPossibili)
    } else {
      setNome(''); setRuolo(''); setSettore(''); setAziendaTipo('')
      setPersonalita(''); setDescrizioneEstesa(''); setDifficolta(2)
      setVoiceId(undefined)
      setObiezioni([]); setObiettivoChiamata(''); setObiettiviPossibili([])
    }
    setNuovaObiezione(''); setNuovoObiettivo(''); setErroreGlobale(null)
  }, [aperto, lead])

  const aggiungiObiezione = () => {
    const v = nuovaObiezione.trim()
    if (v && !obiezioni.includes(v) && obiezioni.length < 10) {
      setObiezioni([...obiezioni, v])
      setNuovaObiezione('')
    }
  }

  const aggiungiObiettivo = () => {
    const v = nuovoObiettivo.trim()
    if (v && !obiettiviPossibili.includes(v) && obiettiviPossibili.length < 10) {
      setObiettiviPossibili([...obiettiviPossibili, v])
      setNuovoObiettivo('')
    }
  }

  const inCorso = crea.isPending || modifica.isPending

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErroreGlobale(null)
    if (personalita.trim().length < 10) {
      setErroreGlobale('La personalità deve avere almeno 10 caratteri.')
      return
    }

    const body: Record<string, unknown> = {
      nome, ruolo, settore, personalita,
      difficolta,
      obiezioni,
    }
    if (aziendaTipo.trim()) body.azienda_tipo = aziendaTipo.trim()
    if (descrizioneEstesa.trim()) body.descrizione_estesa = descrizioneEstesa.trim()
    if (obiettivoChiamata.trim()) body.obiettivo_chiamata = obiettivoChiamata.trim()
    if (obiettiviPossibili.length > 0) body.obiettivi_possibili = obiettiviPossibili
    if (voiceId) body.voice_id = voiceId

    try {
      if (editing && lead) {
        await modifica.mutateAsync({ id: lead.id, dati: body })
        toast.successo(`Lead "${nome}" aggiornato`)
      } else {
        await crea.mutateAsync(body)
        toast.successo(`Lead "${nome}" creato con successo`)
      }
      onSalvato()
    } catch (err) {
      setErroreGlobale(messaggioErrore(err))
    }
  }

  const titolo = editing
    ? `Modifica lead — ${lead?.nome}`
    : rimanenti != null
      ? `Nuovo Lead · ${rimanenti} rimanenti`
      : 'Nuovo Lead'

  return (
    <Drawer
      aperto={aperto}
      titolo={titolo}
      onChiudi={onChiudi}
      larghezza={520}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variante="ghost" onClick={onChiudi}>Annulla</Button>
          <Button
            type="submit"
            form="mio-lead-form"
            iconaSinistra={<Save size={14} />}
            caricamento={inCorso}
          >
            {editing ? 'Salva modifiche' : 'Crea lead'}
          </Button>
        </div>
      }
    >
      <form id="mio-lead-form" onSubmit={submit} className={styles.form}>
        <Sezione titolo="Dati anagrafici">
          <div className={styles.griglia2}>
            <Campo label="Nome *" valore={nome} onChange={setNome} required minLength={2} />
            <Campo label="Ruolo *" valore={ruolo} onChange={setRuolo} required minLength={2} placeholder="es. CFO" />
            <Campo label="Settore *" valore={settore} onChange={setSettore} required minLength={2} placeholder="es. Finance" />
            <Campo label="Tipo azienda" valore={aziendaTipo} onChange={setAziendaTipo} placeholder="es. Multinazionale" />
          </div>
        </Sezione>

        <Sezione titolo="Profilo comportamentale">
          <CampoTextarea
            label="Personalità *"
            valore={personalita}
            onChange={setPersonalita}
            rows={3}
            placeholder='es. "Scettico sui costi, di fretta, va al punto"'
            maxLength={500}
            required
            minLength={10}
          />

          <div>
            <label className={styles.label}>Difficoltà</label>
            <div className={styles.difficolta}>
              {[1, 2, 3].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`${styles.diffBtn} ${difficolta === d ? styles.diffSelez : ''}`}
                  onClick={() => setDifficolta(d as 1 | 2 | 3)}
                >
                  {d === 1 ? '○ Facile' : d === 2 ? '◐ Medio' : '● Difficile'}
                </button>
              ))}
            </div>
          </div>
        </Sezione>

        <Sezione titolo="Obiezioni tipiche">
          <TagList
            items={obiezioni}
            onRemove={(i) => setObiezioni(obiezioni.filter((_, idx) => idx !== i))}
            nuovo={nuovaObiezione}
            setNuovo={setNuovaObiezione}
            onAggiungi={aggiungiObiezione}
            placeholder='es. "Troppo caro", "Ci penso"'
            limite={10}
          />
        </Sezione>

        <Sezione titolo="Obiettivo della chiamata">
          <Campo
            label="Obiettivo default"
            valore={obiettivoChiamata}
            onChange={setObiettivoChiamata}
            placeholder="es. Fissare una demo"
            maxLength={200}
          />
          <div>
            <label className={styles.label}>Obiettivi selezionabili</label>
            <TagList
              items={obiettiviPossibili}
              onRemove={(i) => setObiettiviPossibili(obiettiviPossibili.filter((_, idx) => idx !== i))}
              nuovo={nuovoObiettivo}
              setNuovo={setNuovoObiettivo}
              onAggiungi={aggiungiObiettivo}
              placeholder="Aggiungi un obiettivo selezionabile"
              limite={10}
            />
          </div>
        </Sezione>

        <Sezione titolo="Descrizione per l'AI (opzionale)">
          <CampoTextarea
            label=""
            valore={descrizioneEstesa}
            onChange={setDescrizioneEstesa}
            rows={4}
            placeholder="Contesto: azienda, situazione, storia recente, motivazioni…"
            maxLength={2000}
          />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8 }}>
            L'AI userà questi dettagli per rendere la simulazione più realistica.
          </div>
        </Sezione>

        <Sezione titolo="Voce ElevenLabs (opzionale)">
          <VoiceSelector valore={voiceId} onChange={setVoiceId} />
        </Sezione>

        {erroreGlobale && (
          <div className={styles.erroreGlobale}>
            <AlertCircle size={14} />
            <span>{erroreGlobale}</span>
          </div>
        )}
      </form>
    </Drawer>
  )
}

// ─── Helper components (stesso stile del LeadForm superadmin) ──────

function Sezione({ titolo, children }: { titolo: string; children: React.ReactNode }) {
  return (
    <div className={styles.sezione}>
      <h3 className={styles.sezioneTitolo}>{titolo}</h3>
      <div className={styles.sezioneCorpo}>{children}</div>
    </div>
  )
}

interface CampoProps {
  label: string
  valore: string
  onChange: (v: string) => void
  placeholder?: string
  required?: boolean
  minLength?: number
  maxLength?: number
}

function Campo({ label, valore, onChange, ...rest }: CampoProps) {
  return (
    <div>
      {label && <label className={styles.label}>{label}</label>}
      <input
        type="text"
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        className={styles.input}
        {...rest}
      />
    </div>
  )
}

function CampoTextarea({ label, valore, onChange, rows = 3, maxLength, ...rest }: CampoProps & { rows?: number }) {
  return (
    <div>
      {(label || maxLength) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          {label && <label className={styles.label}>{label}</label>}
          {maxLength && <span className={styles.counter}>{valore.length} / {maxLength}</span>}
        </div>
      )}
      <textarea
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        maxLength={maxLength}
        className={`${styles.input} ${styles.textarea}`}
        {...rest}
      />
    </div>
  )
}

function TagList({ items, onRemove, nuovo, setNuovo, onAggiungi, placeholder, limite }: {
  items: string[]
  onRemove: (idx: number) => void
  nuovo: string
  setNuovo: (v: string) => void
  onAggiungi: () => void
  placeholder?: string
  limite: number
}) {
  return (
    <div>
      {items.length > 0 && (
        <div className={styles.tags}>
          {items.map((t, i) => (
            <span key={`${t}-${i}`} className={styles.tag}>
              {t}
              <button type="button" onClick={() => onRemove(i)} aria-label="Rimuovi">×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="text"
          value={nuovo}
          onChange={(e) => setNuovo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onAggiungi() }
          }}
          placeholder={placeholder}
          className={styles.input}
          style={{ flex: 1 }}
          disabled={items.length >= limite}
          maxLength={200}
        />
        <Button
          type="button"
          variante="secondary"
          onClick={onAggiungi}
          disabled={!nuovo.trim() || items.length >= limite}
        >+</Button>
      </div>
      {items.length >= limite && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Limite raggiunto ({limite}).
        </div>
      )}
    </div>
  )
}
